import React, { useState, useEffect } from 'react';
import './AboutMuayThai.css';

function AboutMuayThai() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [activeMoveIndex, setActiveMoveIndex] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Scroll detection for navigation
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > window.innerHeight * 0.5);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-rotate counter wheel
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveMoveIndex((prev) => (prev + 1) % 8);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    }, 3000);
  };

  const handleFormChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const programs = [
    {
      label: 'Fundamentals',
      title: 'Basics That Hold Up',
      description: 'Build a solid foundation in Muay Thai stance, footwork, and basic striking techniques.',
      bullets: ['Stance & footwork', 'Punches, kicks, elbows, knees', 'Pad holding & partner drills'],
      image: '/images/program-fundamentals.jpg',
    },
    {
      label: 'Fight Conditioning',
      title: 'Engine Building',
      description: 'Develop the cardio, strength, and endurance needed for serious training.',
      bullets: ['Heavy bag rounds', 'Clinch endurance', 'Strength & mobility'],
      image: '/images/program-conditioning.jpg',
    },
    {
      label: 'Sparring & Fight Prep',
      title: 'Ready for the Ring',
      description: 'Take your skills to the next level with controlled sparring and fight strategy.',
      bullets: ['Controlled sparring', 'Fight strategy', 'Peaking & corner work'],
      image: '/images/program-sparring.jpg',
    },
  ];

  const movePairs = [
    { move: 'Jab', counter: 'Cross', icon: '👊' },
    { move: 'Cross', counter: 'Slip + Hook', icon: '🥊' },
    { move: 'Hook', counter: 'Block + Elbow', icon: '💪' },
    { move: 'Uppercut', counter: 'Lean Back', icon: '⬆️' },
    { move: 'Round Kick', counter: 'Check + Teep', icon: '🦵' },
    { move: 'Teep', counter: 'Catch + Sweep', icon: '🦶' },
    { move: 'Knee', counter: 'Clinch Control', icon: '🦵' },
    { move: 'Elbow', counter: 'Distance + Counter', icon: '💥' },
  ];

  const scheduleData = [
    { time: '6:00 AM', class: 'Morning Conditioning', level: 'All Levels' },
    { time: '7:30 AM', class: 'Fundamentals', level: 'Beginner' },
    { time: '12:00 PM', class: 'Open Gym', level: 'All Levels' },
    { time: '5:30 PM', class: 'Pad Work', level: 'Intermediate' },
    { time: '7:00 PM', class: 'Sparring', level: 'Advanced' },
    { time: '8:30 PM', class: 'Clinch Technique', level: 'All Levels' },
  ];

  const filters = ['All', 'Fundamentals', 'Conditioning', 'Sparring'];

  const filteredSchedule = activeFilter === 'All'
    ? scheduleData
    : scheduleData.filter(item =>
        item.class.toLowerCase().includes(activeFilter.toLowerCase()) ||
        (activeFilter === 'Fundamentals' && item.level === 'Beginner') ||
        (activeFilter === 'Conditioning' && item.class.includes('Conditioning')) ||
        (activeFilter === 'Sparring' && item.class.includes('Sparring'))
      );

  const plans = [
    {
      name: 'Drop-In',
      price: '$25',
      period: 'per session',
      description: 'Perfect for visitors or occasional training.',
      features: ['Single class access', 'All equipment provided', 'No commitment'],
      highlighted: false,
    },
    {
      name: 'Monthly',
      price: '$149',
      period: 'per month',
      description: 'Unlimited training for serious practitioners.',
      features: ['Unlimited classes', 'Open gym access', 'Gear storage', 'Member events'],
      highlighted: true,
    },
    {
      name: 'Fight Camp',
      price: '$299',
      period: 'per month',
      description: 'For competitors and advanced students.',
      features: ['Everything in Monthly', '2 private sessions/month', 'Fight prep coaching', 'Corner work included'],
      highlighted: false,
    },
  ];

  return (
    <div className="about-muaythai-modern">
      {/* Navigation */}
      <nav className={`muaythai-nav ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="nav-logo">
            Muay Thai Academy
          </button>
          <div className="nav-links">
            <button onClick={() => scrollToSection('programs')}>Programs</button>
            <button onClick={() => scrollToSection('schedule')}>Schedule</button>
            <button onClick={() => scrollToSection('membership')}>Membership</button>
            <button onClick={() => scrollToSection('contact')}>Contact</button>
            <button onClick={() => scrollToSection('contact')} className="btn-primary-nav">
              Book a Class
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-bg">
          <img src="/images/hero-train.jpg" alt="Muay Thai Training" className="hero-image" />
          <div className="hero-overlay" />
        </div>
        <div className="hero-content">
          <h1 className="hero-headline">
            <span className="headline-line">Train</span>
            <span className="headline-line red-text">Hard.</span>
          </h1>
          <p className="hero-subhead">
            Muay Thai fundamentals, pad work, and conditioning for all levels.
            Master the Art of Eight Limbs.
          </p>
          <div className="hero-cta">
            <button onClick={() => scrollToSection('contact')} className="btn-primary">
              Book a Free Class →
            </button>
            <button onClick={() => scrollToSection('schedule')} className="btn-link">
              View Schedule
            </button>
          </div>
        </div>
        <div className="poster-strip-right">
          <p className="mono-label">Bangkok-Style Training</p>
          <p className="strip-text">Clinch • Kicks • Conditioning</p>
        </div>
      </section>

      {/* Fight Smart Section */}
      <section id="fight-smart" className="hero-section fight-smart">
        <div className="hero-bg">
          <img src="/images/hero-fight-smart.jpg" alt="Muay Thai Clinch" className="hero-image" />
          <div className="hero-overlay-right" />
        </div>
        <div className="hero-content-right">
          <h1 className="hero-headline">
            <span className="headline-line">Fight</span>
            <span className="headline-line red-text">Smart.</span>
          </h1>
          <p className="hero-subhead">
            Tactics, timing, and ring craft—built on real sparring and fight prep.
            Every move has a counter. Timing beats speed.
          </p>
          <div className="hero-cta">
            <button onClick={() => scrollToSection('instructors')} className="btn-primary">
              Meet the Coaches
            </button>
            <button onClick={() => scrollToSection('programs')} className="btn-link">
              Programs →
            </button>
          </div>
        </div>
        <div className="poster-strip-left">
          <p className="mono-label">Fight Prep Track</p>
          <p className="strip-text">Sparring • Strategy • Peaking</p>
        </div>
      </section>

      {/* Counter Wheel Section */}
      <section id="counter-wheel" className="counter-wheel-section">
        <div className="section-header">
          <p className="mono-label">The Principle</p>
          <h2 className="section-title">
            Every Move Has<br />
            <span className="red-text">A Counter</span>
          </h2>
          <p className="section-description">
            In Muay Thai, no technique is unbeatable. Understanding the counter-game
            is what separates good fighters from great ones.
          </p>
        </div>
        <div className="counter-wheel-container">
          <div className="counter-wheel">
            <svg className="wheel-lines">
              {movePairs.map((_, index) => {
                const angle = (index / movePairs.length) * 360 - 90;
                const rad = (angle * Math.PI) / 180;
                const x1 = 50 + 20 * Math.cos(rad);
                const y1 = 50 + 20 * Math.sin(rad);
                const x2 = 50 + 40 * Math.cos(rad);
                const y2 = 50 + 40 * Math.sin(rad);
                const isActive = activeMoveIndex === index;

                return (
                  <line
                    key={`line-${index}`}
                    x1={`${x1}%`}
                    y1={`${y1}%`}
                    x2={`${x2}%`}
                    y2={`${y2}%`}
                    stroke={isActive ? '#FF2E2E' : 'rgba(245, 247, 250, 0.2)'}
                    strokeWidth={isActive ? 3 : 1}
                  />
                );
              })}
            </svg>
            <div className="wheel-center">
              <div className="center-circle">
                <span className="center-text">TIME</span>
                <span className="center-text-small">ING</span>
              </div>
              <p className="center-label">The Ultimate Principle</p>
            </div>
            {movePairs.map((pair, index) => {
              const angle = (index / movePairs.length) * 360 - 90;
              const rad = (angle * Math.PI) / 180;
              const x = 50 + 40 * Math.cos(rad);
              const y = 50 + 40 * Math.sin(rad);
              const isActive = activeMoveIndex === index;

              return (
                <div
                  key={index}
                  className={`move-item ${isActive ? 'active' : ''}`}
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                  }}
                >
                  <div className={`move-card ${isActive ? 'active' : ''}`}>
                    <span className="move-icon">{pair.icon}</span>
                    <span className="move-name">{pair.move}</span>
                  </div>
                  {isActive && (
                    <div className="counter-tooltip">
                      <p className="tooltip-label">Counter With</p>
                      <p className="tooltip-counter">{pair.counter}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="counter-explanation">
          <p className="explanation-text">
            Hover over any technique to see its counter. Remember: knowing the counter
            is only half the battle—<span className="red-text bold">timing</span> is what makes it work.
          </p>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="programs-section">
        <div className="section-container">
          <div className="section-header-left">
            <h2 className="section-title-large strike-rule">Programs</h2>
            <div className="red-rule" />
          </div>
          <p className="section-intro">
            Choose a path. Show up. We'll handle the progress. Every program is designed
            to take you from where you are to where you want to be.
          </p>
          <div className="programs-grid">
            {programs.map((program, index) => (
              <div key={program.label} className={`program-card ${index % 2 === 0 ? '' : 'reverse'}`}>
                <div className="program-image">
                  <img src={program.image} alt={program.title} />
                </div>
                <div className="program-content">
                  <p className="mono-label">{program.label}</p>
                  <h3 className="program-title">{program.title}</h3>
                  <p className="program-description">{program.description}</p>
                  <ul className="program-bullets">
                    {program.bullets.map((bullet, i) => (
                      <li key={i}>
                        <span className="bullet-dot" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                  <button className="program-link">Details →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instructors Section */}
      <section id="instructors" className="hero-section instructors-section">
        <div className="hero-bg">
          <img src="/images/instructors-bg.jpg" alt="Muay Thai Instructors" className="hero-image" />
          <div className="hero-overlay" />
        </div>
        <div className="hero-content">
          <h1 className="hero-headline">
            <span className="headline-line">World</span>
            <span className="headline-line">Class</span>
            <span className="headline-line red-text">Coaches.</span>
          </h1>
          <p className="hero-subhead">
            Competition experience, technical detail, and a system that scales from
            day-one to fight-night. Learn from fighters who've been there.
          </p>
          <button onClick={() => scrollToSection('contact')} className="btn-primary">
            See the Team
          </button>
        </div>
        <div className="poster-strip-right">
          <p className="mono-label">Coaching Staff</p>
          <p className="strip-text">Striking / Clinch / Strength</p>
        </div>
      </section>

      {/* Schedule Section */}
      <section id="schedule" className="schedule-section">
        <div className="section-container">
          <div className="section-header-left">
            <h2 className="section-title-large strike-rule">Weekly Schedule</h2>
            <div className="red-rule" style={{ width: '60%' }} />
          </div>
          <div className="schedule-grid">
            <div className="schedule-table">
              <div className="filter-tabs">
                {filters.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setActiveFilter(filter)}
                    className={`filter-tab ${activeFilter === filter ? 'active' : ''}`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
              <div className="schedule-rows">
                {filteredSchedule.map((item, index) => (
                  <div key={index} className="schedule-row">
                    <div className="schedule-time">
                      <span className="time-icon">🕐</span>
                      <span>{item.time}</span>
                    </div>
                    <div className="schedule-class">
                      <span className="class-name">{item.class}</span>
                    </div>
                    <div className="schedule-level">
                      <span className={`level-badge ${item.level.toLowerCase().replace(' ', '-')}`}>
                        {item.level}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="schedule-sidebar">
              <div className="sidebar-card">
                <p className="mono-label">Today at the Gym</p>
                <div className="sidebar-item">
                  <div className="sidebar-icon">🔥</div>
                  <div>
                    <p className="sidebar-title">Next Class</p>
                    <p className="sidebar-text">Pad Work at 5:30 PM</p>
                  </div>
                </div>
                <div className="sidebar-item">
                  <div className="sidebar-icon">👥</div>
                  <div>
                    <p className="sidebar-title">Open Mats</p>
                    <p className="sidebar-text">12:00 PM - 2:00 PM</p>
                  </div>
                </div>
              </div>
              <div className="sidebar-card highlight">
                <p className="sidebar-title">New to Muay Thai?</p>
                <p className="sidebar-text">
                  Start with our Fundamentals class. No experience needed.
                </p>
                <button onClick={() => scrollToSection('contact')} className="btn-primary">
                  Book a Free Trial
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section id="membership" className="membership-section">
        <div className="section-container">
          <div className="section-header-left">
            <h2 className="section-title-large strike-rule">Membership</h2>
            <div className="red-rule" style={{ width: '55%' }} />
          </div>
          <p className="section-intro">
            No contracts. No hidden fees. Just train. Choose the plan that fits your goals.
          </p>
          <div className="membership-grid">
            {plans.map((plan, index) => (
              <div key={plan.name} className={`membership-card ${plan.highlighted ? 'highlighted' : ''}`}>
                {plan.highlighted && (
                  <div className="popular-badge">⭐ Most Popular</div>
                )}
                <div className="membership-header">
                  <p className="mono-label">{plan.name}</p>
                  <div className="membership-price">
                    <span className="price-amount">{plan.price}</span>
                    <span className="price-period">{plan.period}</span>
                  </div>
                </div>
                <p className="membership-description">{plan.description}</p>
                <ul className="membership-features">
                  {plan.features.map((feature, i) => (
                    <li key={i}>
                      <span className="check-icon">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button onClick={() => scrollToSection('contact')} className={`membership-button ${plan.highlighted ? 'primary' : ''}`}>
                  {plan.name === 'Drop-In' ? 'Book a Class' : plan.name === 'Monthly' ? 'Start Membership' : 'Apply Now'}
                </button>
              </div>
            ))}
          </div>
          <p className="membership-footer">
            No contracts. Cancel anytime. All memberships include a 7-day free trial.
          </p>
        </div>
      </section>

      {/* Gym Section */}
      <section id="gym" className="hero-section gym-section">
        <div className="hero-bg">
          <img src="/images/gym-space.jpg" alt="Muay Thai Gym" className="hero-image" />
          <div className="hero-overlay-right" />
        </div>
        <div className="hero-content-right">
          <h1 className="hero-headline">
            <span className="headline-line">Clean</span>
            <span className="headline-line">Space.</span>
            <span className="headline-line red-text">Real Work.</span>
          </h1>
          <p className="hero-subhead">
            Showers, wraps, and everything you need—so you can focus on the rounds.
            Our facility is designed for serious training.
          </p>
          <button onClick={() => scrollToSection('contact')} className="btn-primary">
            Take a Tour
          </button>
        </div>
        <div className="poster-strip-left">
          <p className="mono-label">Amenities</p>
          <div className="amenities-list">
            <div className="amenity-item">🚿 Showers</div>
            <div className="amenity-item">📦 Gear Rental</div>
            <div className="amenity-item">🚗 Parking</div>
            <div className="amenity-item">📍 Location</div>
          </div>
        </div>
      </section>

      {/* Fight Camp Section */}
      <section id="fight-camp" className="hero-section fight-camp">
        <div className="hero-bg">
          <img src="/images/fight-camp.jpg" alt="Muay Thai Fight Camp" className="hero-image" />
          <div className="hero-overlay" />
        </div>
        <div className="hero-content">
          <h1 className="hero-headline">
            <span className="headline-line red-text">Ready?</span>
          </h1>
          <p className="hero-subhead">
            Start with a free class. No gear required. Just show up. We'll take care
            of the rest.
          </p>
          <div className="hero-cta">
            <button onClick={() => scrollToSection('contact')} className="btn-primary">
              Book a Free Class →
            </button>
            <button onClick={() => scrollToSection('contact')} className="btn-link">
              💬 Message Us
            </button>
          </div>
        </div>
        <div className="poster-strip-right">
          <p className="mono-label">Location</p>
          <div className="location-info">
            <p>123 Training Ave</p>
            <p>Mon–Fri 6am–10pm</p>
            <p>Sat–Sun 8am–4pm</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <div className="contact-header">
            <h2 className="section-title-large">Contact</h2>
            <p className="section-description">
              Have questions? Want to book a class? Drop us a message and we'll get back to you.
            </p>
          </div>
          <div className="contact-grid">
            <form onSubmit={handleFormSubmit} className="contact-form">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                  placeholder="Your name"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                  placeholder="your@email.com"
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div className="form-group">
                <label>Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  required
                  rows={4}
                  placeholder="How can we help you?"
                />
              </div>
              <button type="submit" className="btn-primary">
                {isSubmitted ? 'Message Sent!' : 'Send Message →'}
              </button>
            </form>
            <div className="contact-info">
              <div className="info-card">
                <h3>Get in Touch</h3>
                <div className="info-item">
                  <div className="info-icon">📍</div>
                  <div>
                    <p className="info-title">Address</p>
                    <p className="info-text">123 Training Ave<br />Downtown, CA 90210</p>
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-icon">✉️</div>
                  <div>
                    <p className="info-title">Email</p>
                    <p className="info-text">hello@muaythaiacademy.com</p>
                  </div>
                </div>
                <div className="info-item">
                  <div className="info-icon">📞</div>
                  <div>
                    <p className="info-title">Phone</p>
                    <p className="info-text">+1 (555) 123-4567</p>
                  </div>
                </div>
              </div>
              <div className="info-card">
                <h3>Follow Us</h3>
                <div className="social-links">
                  <button type="button" className="social-link">📷 Instagram</button>
                  <button type="button" className="social-link">👥 Facebook</button>
                </div>
              </div>
            </div>
          </div>
          <footer className="contact-footer">
            <p className="footer-logo">Muay Thai Academy</p>
            <p className="footer-copyright">© 2026 Muay Thai Academy. All rights reserved.</p>
            <div className="footer-links">
              <button type="button" className="footer-link">Privacy</button>
              <button type="button" className="footer-link">Terms</button>
            </div>
          </footer>
        </div>
      </section>
    </div>
  );
}

export default AboutMuayThai;
